import { atom } from "jotai";
import { assetLimitsAtom_SUSPENDS } from "../apps";
import {
  numberOfUniqueChatbotNodesAtom,
  numberOfUniqueInterfaceNodesAtom,
} from "../nodes";
import { isTemplatePageAtom } from "./isTemplatePageAtom";

interface UpgradeBannerProps {
  title: string;
  description: string;
  needsToUpgradeInterfaces?: boolean;
  needsToUpgradeChatbots?: boolean;
}

export const upgradeBannerPropsAtom_SUSPENDS = atom<
  Promise<UpgradeBannerProps | null>
>(async (get) => {
  const assetLimits = await get(assetLimitsAtom_SUSPENDS);

  if (!assetLimits?.results) {
    return null;
  }

  const { results } = assetLimits;
  const isTemplatePage = get(isTemplatePageAtom);
  const systemOrTemplate = isTemplatePage ? "template" : "system";

  // Check if any asset type cannot be created using the can_create field
  const needsToUpgradeInterfaces =
    results.interfaces && !results.interfaces.can_create;
  const needsToUpgradeChatbots =
    results.chatbots && !results.chatbots.can_create;

  if (needsToUpgradeInterfaces && !needsToUpgradeChatbots) {
    const numberOfInterfaceNodes = get(numberOfUniqueInterfaceNodesAtom);
    const requestedCount =
      results.interfaces?.requested_count || numberOfInterfaceNodes;
    return {
      title: "You have reached your Interfaces limit",
      description: `${
        requestedCount === 1
          ? "A new interface"
          : `${requestedCount} interfaces`
      } will be created that will put you over your plans limit. Upgrade to use this ${systemOrTemplate}.`,
      needsToUpgradeInterfaces: true,
      needsToUpgradeChatbots: false,
    };
  }

  if (needsToUpgradeChatbots && !needsToUpgradeInterfaces) {
    const numberOfChatbotNodes = get(numberOfUniqueChatbotNodesAtom);
    const requestedCount =
      results.chatbots?.requested_count || numberOfChatbotNodes;
    return {
      title: "You have reached your Chatbots limit",
      description: `${
        requestedCount === 1 ? "A new chatbot" : `${requestedCount} chatbots`
      } will be created that will put you over your plans limit. Upgrade to use this ${systemOrTemplate}.`,
      needsToUpgradeInterfaces: false,
      needsToUpgradeChatbots: true,
    };
  }

  // Multiple upgrades - when we reach this if, both needsToUpgradeInterfaces AND needsToUpgradeChatbots are guaranteed to be true
  if (needsToUpgradeInterfaces && needsToUpgradeChatbots) {
    return {
      title: `Upgrade to build this ${systemOrTemplate}`,
      description: `New assets will be created that will put you over your plans limit. Upgrade to use this ${systemOrTemplate}.`,
      needsToUpgradeInterfaces: true,
      needsToUpgradeChatbots: true,
    };
  }

  return null;
});

upgradeBannerPropsAtom_SUSPENDS.debugLabel = "upgradeBannerPropsAtom_SUSPENDS";
